import { PureComponent } from "react";

import DateInput from "~/common/components/DateInput";
import { type ProductData } from "~/domains/product/types/ProductData";
import { type CustomDispatch } from "~/app/store/types";
import { connect } from "react-redux";
import { atcPopupupdateDate, atcPopupupdateQty } from "~/features/contracts/redux/actions";
import AtcProductInfo from "~/features/addToCart/components/v2/AtcProductInfo/AtcProductInfo";
import Modal from "~/common/components/Modal";
import { IconType } from "~/common/components/icon/IconType";
import { type RootState } from "~/app/store";
import { Trans, type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { contractIsDayDisabled, getContractYears } from "~/common/utils/dateUtils";
import { type NetPriceData } from "~/domains/pricing/types/PriceData";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import { Button, Stack } from "~/common/mui";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/mui/Alert/Alert";
import AtcProductPriceInfo from "~/features/addToCart/components/v2/AtcProductPriceInfo/AtcProductPriceInfo";

export interface PopupData {
  quantity: number;
  requestedDeliveryDate: Date;
}

export enum Status {
  Active,
  Waiting,
  Completed,
  Cancelled,
}

export const STATUS_CODES_MAP = {
  "codes.lineItemStatus.01": Status.Waiting,
  "codes.lineItemStatus.02": Status.Active,
  "codes.lineItemStatus.05": Status.Completed,
  "codes.lineItemStatus.06": Status.Completed,
  "codes.lineItemStatus.07": Status.Cancelled,
};

const MESSAGE_MAP = {
  [Status.Active]: "contractDetails.atc.remainingQuantity",
  [Status.Waiting]: "contractDetails.atc.waiting",
  [Status.Completed]: "contractDetails.atc.completed",
  [Status.Cancelled]: "contractDetails.atc.cancelled",
};

interface SelfProps extends WithTranslation {
  product: ProductData;
  packageCode: string;
  packageSize: number;

  onCancel: () => void;
  onOk: (data: PopupData) => void;

  editMode?: boolean;
  contractDetails: ContractDetailsResponse;
}

interface FromStateProps {
  selectedQty?: number;
  selectedDate?: Date;
}

interface DispatchProps {
  updateQty: (newQty: number) => void;
  updateDate: (newDate: Date) => void;
}

interface ContractVersionContentsState {
  today: Date;
}

type ContractVersionContentsProps = SelfProps & FromStateProps & DispatchProps;

class ContractVersionContents extends PureComponent<ContractVersionContentsProps, ContractVersionContentsState> {
  constructor(props: ContractVersionContentsProps) {
    super(props);

    this.state = {
      today: new Date(),
    };
  }

  sendData = () => {
    const { selectedQty, selectedDate: requestedDeliveryDate, packageSize } = this.props;
    const quantity = selectedQty || packageSize;
    if (quantity !== undefined && requestedDeliveryDate !== undefined) {
      this.props.onOk({
        quantity,
        requestedDeliveryDate,
      });
    }
  };

  isDayDisabled = (date: Date) => {
    return contractIsDayDisabled(this.props.contractDetails, date);
  };

  render() {
    const { product, t, onCancel, editMode, packageSize, packageCode } = this.props;
    // const packagingInformation = product.packagingInformation;

    const quantity = this.props.selectedQty || packageSize;
    const requestedDeliveryDate = this.props.selectedDate;

    const isPackageSizeInvalid = !quantity || !Number.isInteger(quantity / packageSize);
    const isDateInvalid = !requestedDeliveryDate;

    const status = this.props.contractDetails.status;

    const willSplit = quantity > this.props.contractDetails.openQuantity;
    const isActive = STATUS_CODES_MAP[status as keyof typeof STATUS_CODES_MAP] === Status.Active;

    const addToCartDisabled = window.SCHA.config.disableAllWS || isPackageSizeInvalid || isDateInvalid;

    const okLabel = t(`addToCartPopup.${editMode ? "edit" : "add"}.submitBtn`);

    const { minYear, maxYear } = getContractYears(this.props.contractDetails);

    const price = this.props.contractDetails.nettoPrice;

    const priceUnit = 1; // I guess contracts have always unit of 1?

    const netPrice: NetPriceData = {
      ...price,
      unit: priceUnit,
      baseValue: price.value, // With no surcharge, base value === value
      surcharge: 0, // Can contracts have surcharge?
      isSpecial: false, // Can contracts have special price?
      isSubscription: !product.quantityVisible,
    };

    return (
      <Modal
        title={t("addToCartPopup.title")}
        onClose={onCancel}
        footerChildren={
          <Stack direction={"row"} gap={1}>
            <Button key={"cancel"} variant={"text"} onClick={onCancel}>
              {t("cancel")}
            </Button>
            <Button
              key={"ok"}
              onClick={this.sendData}
              startIcon={<DefaultButtonIcon type={IconType.Cart} />}
              disabled={addToCartDisabled}
            >
              {okLabel}
            </Button>
          </Stack>
        }
      >
        <div className="scha__atc-popup scha__atc-popup-continer">
          <div className="scha__atc-popup--column-info">
            <AtcProductInfo descriptor={fakeDescriptorFromProduct(product)} variant="CONTRACT" />
          </div>
          <div className={"scha__atc-popup--column-packaging__wrapper scha__atc-popup--column-packaging"}>
            <div>
              <DateInput
                label={t("addToCart.contract.rdd")}
                data-test-id={"add-to-cart-date-selector-rdd"}
                date={requestedDeliveryDate ?? null}
                isInvalid={isDateInvalid}
                onChange={this.props.updateDate}
                isDayDisabled={this.isDayDisabled}
                maxYear={maxYear}
                minYear={minYear}
                showErrorIcon={isDateInvalid && true}
                helperLabel={isDateInvalid && t("addToCartPopup.date.error")}
              />

              {isPackageSizeInvalid && (
                <Alert className="mt-3" severity="error">
                  {t("addToCartPopup.mismatchedQty")}
                </Alert>
              )}

              {(!isActive || willSplit) && (
                <Alert className="mt-3" severity="info">
                  <Trans
                    i18nKey={MESSAGE_MAP[STATUS_CODES_MAP[status as keyof typeof STATUS_CODES_MAP] as Status]}
                    values={{ openQuantity: this.props.contractDetails.openQuantity }}
                  >
                    Your remaining quantity for this contract is <b>{this.props.contractDetails.openQuantity}.</b>
                  </Trans>

                  <p>
                    {t(isActive ? "contractDetails.atc.additionalQuantity" : "contractDetails.atc.regularConditions")}
                  </p>
                </Alert>
              )}
            </div>
            <div>
              <AtcProductPriceInfo
                descriptor={fakeDescriptorFromProduct(product)}
                quantity={quantity || packageSize}
                price={netPrice}
                priceUnit={priceUnit}
                onChange={this.props.updateQty}
                invalidQuantity={isPackageSizeInvalid}
                priceReference={""}
                variant="CONTRACT"
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state: RootState): FromStateProps => {
  return {
    selectedDate: state.contracts.popupData.selectedDate,
    selectedQty: state.contracts.popupData.selectedQty,
  };
};

const mapDispatchToProps = (dispatch: CustomDispatch<RootState>): DispatchProps => {
  return {
    updateQty: (newQty) => dispatch(atcPopupupdateQty(newQty)),
    updateDate: (newDate) => dispatch(atcPopupupdateDate(newDate)),
  };
};

export default withTranslationBase()(connect(mapStateToProps, mapDispatchToProps)(ContractVersionContents));
